import { planInfo, type PlanInfo } from '@adop/shared/plans';

/**
 * Honest plan usage meter (no billing, no order blocking).
 *
 * The plan catalog lives in @adop/shared/plans; an unknown plan string is shown
 * verbatim and never silently treated as a real tier. `includedOrdersPerMonth`
 * of null means unlimited.
 */

export interface PlanUsage {
  /** Raw plan string from the database (may be unknown or null). */
  plan: string | null;
  /** Label to show: the known plan label, the raw unknown value, or "No plan". */
  label: string;
  /** False when the plan string is absent or not in the catalog. */
  known: boolean;
  /** Catalog entry, or null when unknown. */
  info: PlanInfo | null;
  /** null = unlimited or unknown. */
  includedOrdersPerMonth: number | null;
  used: number;
  /** null when unlimited or unknown. */
  remaining: number | null;
  /** True only for a known, limited plan whose volume has been exceeded. */
  over: boolean;
  /** 0–100 progress for the meter, or null when unlimited/unknown. */
  percentUsed: number | null;
}

function normalizeCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}

export function computePlanUsage(plan: string | null | undefined, used: number): PlanUsage {
  const count = normalizeCount(used);
  const info = planInfo(plan ?? null);

  if (!info) {
    const raw = typeof plan === 'string' ? plan.trim() : '';
    return {
      plan: raw.length > 0 ? raw : null,
      label: raw.length > 0 ? raw : 'No plan',
      known: false,
      info: null,
      includedOrdersPerMonth: null,
      used: count,
      remaining: null,
      over: false,
      percentUsed: null,
    };
  }

  const included = info.includedOrdersPerMonth;
  if (included === null) {
    return {
      plan: info.id,
      label: info.label,
      known: true,
      info,
      includedOrdersPerMonth: null,
      used: count,
      remaining: null,
      over: false,
      percentUsed: null,
    };
  }

  return {
    plan: info.id,
    label: info.label,
    known: true,
    info,
    includedOrdersPerMonth: included,
    used: count,
    remaining: Math.max(0, included - count),
    over: count > included,
    percentUsed: included > 0 ? Math.min(100, Math.round((count / included) * 100)) : 100,
  };
}
